'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Brain, TrendingUp, Award, Flame, BarChart2, Play, CheckCircle, Clock, RefreshCw, Info, Star, Activity, Shield, Cpu, AlertTriangle, ArrowUp, ArrowDown, Minus,  } from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { getCognitiveProfile, upsertCognitiveProfile, saveBrainGymSession, getRecentGymSessions, getTodayPlan, saveDailyPlan, markTaskComplete, getProgressSnapshots, saveProgressSnapshot, generateDailyPlan, detectPlateau, updateProfileFromSession, computeCognitiveFingerprint, createDefaultProfile, DEFAULT_PARAMS, type CognitiveProfile, type DailyTrainingPlan, type TrainingTask, type BrainGymSession, type ProgressSnapshot, type GameType, type DifficultyLevel, type CognitiveStateLabel, type CognitiveFingerprint,  } from '@/lib/services/brainGymService';
import { GoNoGoGame, NBackGame, TaskSwitchingGame, type GameResult } from './BrainGymGames';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATE_CONFIG: Record<CognitiveStateLabel, { color: string; bg: string; icon: string; label: string }> = {
  focused:   { color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: '🎯', label: 'Focused' },
  elevated:  { color: '#38BDF8', bg: 'rgba(56,189,248,0.12)', icon: '⚡', label: 'Elevated' },
  balanced:  { color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)', icon: '⚖️', label: 'Balanced' },
  recovering:{ color: '#F97316', bg: 'rgba(249,115,22,0.12)', icon: '🔄', label: 'Recovering' },
  fatigued:  { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  icon: '😴', label: 'Fatigued' },
  stressed:  { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: '⚠️', label: 'Stressed' },
};

const GAME_CONFIG: Record<GameType, { label: string; icon: string; color: string; desc: string }> = {
  go_no_go:       { label: 'Attention & Inhibition', icon: '🎯', color: '#10B981', desc: 'Go/No-Go paradigm — tap on target, inhibit on distractor' },
  n_back:         { label: 'Working Memory',         icon: '🧩', color: '#8B5CF6', desc: 'N-Back task — identify matching positions in sequence' },
  task_switching: { label: 'Cognitive Flexibility',  icon: '🔀', color: '#38BDF8', desc: 'Rule-switching task — adapt to changing cognitive rules' },
};

const DIFF_COLOR: Record<DifficultyLevel, string> = {
  easy: '#10B981', medium: '#F97316', hard: '#EF4444',
};

function ScoreRing({ value, size = 72, color = '#F97316', label }: {
  value: number; size?: number; color?: string; label: string;
}) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / 100, 1);
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative">
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(30,58,95,0.5)" strokeWidth={5} />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[14px] font-bold text-white">{Math.round(value)}</span>
        </div>
      </div>
      <span className="text-[10px] text-[#8BAFD4] text-center leading-tight">{label}</span>
    </div>
  );
}

function DeltaBadge({ delta }: { delta?: number }) {
  if (delta == null) return null;
  const abs = Math.abs(Math.round(delta));
  if (abs < 1) return <span className="text-[10px] text-zinc-500 flex items-center gap-0.5"><Minus size={9} />0%</span>;
  if (delta > 0) return <span className="text-[10px] text-emerald-400 flex items-center gap-0.5"><ArrowUp size={9} />+{abs}%</span>;
  return <span className="text-[10px] text-red-400 flex items-center gap-0.5"><ArrowDown size={9} />-{abs}%</span>;
}

// ─── View: Daily Plan ─────────────────────────────────────────────────────────

function DailyPlanView({
  plan, profile, onStartTask,
}: {
  plan: DailyTrainingPlan;
  profile: CognitiveProfile;
  onStartTask: (task: TrainingTask) => void;
}) {
  const state = STATE_CONFIG[plan.cognitiveState] ?? STATE_CONFIG.balanced;
  const completedCount = plan.tasks.filter(t => t.completed).length;
  const totalDuration = plan.tasks.reduce((a, t) => a + t.estimatedDurationSec, 0);

  return (
    <div className="space-y-4">
      {/* State banner */}
      <div className="rounded-2xl p-4 border flex items-center gap-4"
        style={{ background: state.bg, borderColor: `${state.color}30` }}>
        <div className="text-3xl">{state.icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider font-medium mb-0.5">Your Brain Today</p>
          <p className="text-[18px] font-bold" style={{ color: state.color }}>{state.label}</p>
          <p className="text-[11px] text-[#8BAFD4] mt-0.5 leading-relaxed">{plan.planRationale}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-[11px] text-[#4A7AB5]">Confidence</p>
          <p className="text-[16px] font-bold text-white">{Math.round(plan.stateConfidence * 100)}%</p>
          <div className="flex gap-1 mt-1 justify-end flex-wrap">
            {plan.stateSources.map(s => (
              <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#1E3A5F]/60 text-[#8BAFD4]">{s}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Plan header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-bold text-white">Today's Training Plan</h3>
          <p className="text-[11px] text-[#4A7AB5] mt-0.5">
            {completedCount}/{plan.totalTasks} tasks · ~{Math.round(totalDuration / 60)} min total
          </p>
        </div>
        {plan.isComplete && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30">
            <CheckCircle size={13} className="text-emerald-400" />
            <span className="text-[11px] font-bold text-emerald-400">Complete!</span>
          </div>
        )}
      </div>

      {/* Task cards */}
      <div className="space-y-3">
        {plan.tasks.map((task, idx) => {
          const gc = GAME_CONFIG[task.gameType];
          const isNext = !task.completed && plan.tasks.slice(0, idx).every(t => t.completed);
          return (
            <div key={task.id}
              className="rounded-2xl border transition-all"
              style={{
                background: task.completed ? 'rgba(16,185,129,0.06)' : isNext ? 'rgba(11,22,40,0.9)' : 'rgba(11,22,40,0.6)',
                borderColor: task.completed ? 'rgba(16,185,129,0.25)' : isNext ? `${gc.color}35` : 'rgba(30,58,95,0.4)',
              }}>
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
                    style={{ background: task.completed ? 'rgba(16,185,129,0.15)' : `${gc.color}15` }}>
                    {task.completed ? '✓' : gc.icon}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[13px] font-bold text-white">{gc.label}</p>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                        style={{ background: `${DIFF_COLOR[task.difficulty]}18`, color: DIFF_COLOR[task.difficulty] }}>
                        {task.difficulty}
                      </span>
                      {task.completed && task.score != null && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-bold">
                          Score: {Math.round(task.score)}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#4A7AB5] mt-0.5">{gc.desc}</p>
                    <p className="text-[10px] text-[#4A7AB5]/70 mt-1 italic">{task.rationale}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Clock size={10} className="text-[#4A7AB5]" />
                      <span className="text-[10px] text-[#4A7AB5]">~{task.estimatedDurationSec}s</span>
                    </div>
                  </div>
                  {/* Action */}
                  {!task.completed && (
                    <button
                      onClick={() => onStartTask(task)}
                      disabled={!isNext && idx > 0}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-bold transition-all active:scale-95 min-h-[40px] flex-shrink-0"
                      style={{
                        background: isNext ? `linear-gradient(135deg, ${gc.color}, ${gc.color}CC)` : 'rgba(30,58,95,0.3)',
                        color: isNext ? 'white' : '#4A7AB5',
                        opacity: !isNext && idx > 0 ? 0.5 : 1,
                      }}>
                      <Play size={11} />
                      {isNext ? 'Start' : 'Locked'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cognitive profile summary */}
      <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
        <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider font-medium mb-3">Your Cognitive Profile</p>
        <div className="grid grid-cols-4 gap-2">
          <ScoreRing value={profile.focusScore} color="#10B981" label="Focus" size={64} />
          <ScoreRing value={profile.mentalEnergy} color="#8B5CF6" label="Memory" size={64} />
          <ScoreRing value={profile.stability} color="#38BDF8" label="Stability" size={64} />
          <ScoreRing value={profile.flexibility} color="#F97316" label="Flexibility" size={64} />
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1E3A5F]/30">
          <div className="flex items-center gap-1.5">
            <Flame size={12} className="text-[#F97316]" />
            <span className="text-[11px] text-[#8BAFD4]">{profile.streakDays} day streak</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Activity size={12} className="text-[#4A7AB5]" />
            <span className="text-[11px] text-[#8BAFD4]">{profile.sessionCount} sessions total</span>
          </div>
          {profile.plateauDetected && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle size={10} className="text-amber-400" />
              <span className="text-[10px] text-amber-400">Plateau detected</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── View: Active Game Session ────────────────────────────────────────────────

function GameSessionView({
  task, profile, onComplete, onExit,
}: {
  task: TrainingTask;
  profile: CognitiveProfile;
  onComplete: (result: GameResult) => void;
  onExit: () => void;
}) {
  const gc = GAME_CONFIG[task.gameType];
  const params = { ...DEFAULT_PARAMS[task.difficulty] };

  // Adjust params based on profile
  if (task.gameType === 'n_back') {
    params.nBackLevel = task.difficulty === 'easy' ? 1 : task.difficulty === 'medium' ? 2 : 3;
  }

  return (
    <div className="space-y-4">
      {/* Session header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
            style={{ background: `${gc.color}15` }}>
            {gc.icon}
          </div>
          <div>
            <p className="text-[14px] font-bold text-white">{gc.label}</p>
            <p className="text-[10px] text-[#4A7AB5]">
              <span className="font-bold" style={{ color: DIFF_COLOR[task.difficulty] }}>{task.difficulty}</span>
              {' '}· Cognitive training support
            </p>
          </div>
        </div>
        <button onClick={onExit}
          className="px-3 py-2 rounded-xl text-[11px] text-[#8BAFD4] border border-[#1E3A5F]/50 hover:border-red-500/40 hover:text-red-400 transition-all min-h-[40px]">
          Exit
        </button>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-[#1E3A5F]/20 border border-[#1E3A5F]/30">
        <Info size={11} className="text-[#4A7AB5] mt-0.5 flex-shrink-0" />
        <p className="text-[10px] text-[#4A7AB5] leading-relaxed">
          Cognitive training support — not medical diagnosis. Based on validated {task.gameType === 'go_no_go' ? 'Go/No-Go' : task.gameType === 'n_back' ? 'N-Back' : 'task-switching'} paradigm.
        </p>
      </div>

      {/* Game area */}
      <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#060D1A]/80 p-5 sm:p-6">
        {task.gameType === 'go_no_go' && (
          <GoNoGoGame params={params} onComplete={onComplete} />
        )}
        {task.gameType === 'n_back' && (
          <NBackGame params={params} onComplete={onComplete} />
        )}
        {task.gameType === 'task_switching' && (
          <TaskSwitchingGame params={params} onComplete={onComplete} />
        )}
      </div>
    </div>
  );
}

// ─── View: Session Result ─────────────────────────────────────────────────────

function SessionResultView({
  task, result, profile, insight, onContinue,
}: {
  task: TrainingTask;
  result: GameResult;
  profile: CognitiveProfile;
  insight: string;
  onContinue: () => void;
}) {
  const gc = GAME_CONFIG[task.gameType];
  const scoreColor = result.score >= 75 ? '#10B981' : result.score >= 50 ? '#F97316' : '#EF4444';
  const scoreLabel = result.score >= 80 ? 'Excellent' : result.score >= 65 ? 'Good' : result.score >= 50 ? 'Fair' : 'Keep Practising';

  return (
    <div className="space-y-4">
      {/* Score hero */}
      <div className="rounded-2xl border p-5 text-center"
        style={{ background: `${scoreColor}08`, borderColor: `${scoreColor}25` }}>
        <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider mb-2">{gc.label} — Result</p>
        <div className="flex items-center justify-center gap-4 mb-3">
          <ScoreRing value={result.score} size={88} color={scoreColor} label="Score" />
          <div className="text-left space-y-2">
            <div>
              <p className="text-[10px] text-[#4A7AB5]">Accuracy</p>
              <p className="text-[18px] font-bold text-white">{result.accuracy}%</p>
            </div>
            <div>
              <p className="text-[10px] text-[#4A7AB5]">Avg Reaction</p>
              <p className="text-[18px] font-bold text-white">{result.reactionTimeAvg}ms</p>
            </div>
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
          style={{ background: `${scoreColor}15`, border: `1px solid ${scoreColor}30` }}>
          <Star size={12} style={{ color: scoreColor }} />
          <span className="text-[12px] font-bold" style={{ color: scoreColor }}>{scoreLabel}</span>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Trials', value: `${result.trialsCorrect}/${result.trialsTotal}`, color: '#38BDF8' },
          { label: 'Duration', value: `${Math.round(result.durationMs / 1000)}s`, color: '#8B5CF6' },
          { label: 'Adaptations', value: `${result.difficultyAdjustments}×`, color: '#F97316' },
        ].map(m => (
          <div key={m.label} className="rounded-xl p-3 text-center border border-[#1E3A5F]/40 bg-[#0B1628]/60">
            <p className="text-[16px] font-bold" style={{ color: m.color }}>{m.value}</p>
            <p className="text-[10px] text-[#4A7AB5]">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Fatigue indicator */}
      {result.fatigueDetected && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/25">
          <AlertTriangle size={13} className="text-amber-400 flex-shrink-0" />
          <p className="text-[11px] text-amber-300">Fatigue pattern detected — next task difficulty adjusted accordingly.</p>
        </div>
      )}

      {/* AI Insight */}
      <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Cpu size={13} className="text-[#F97316]" />
          <p className="text-[11px] font-bold text-[#F97316] uppercase tracking-wider">Insight</p>
        </div>
        <p className="text-[12px] text-[#8BAFD4] leading-relaxed">{insight}</p>
      </div>

      <button onClick={onContinue}
        className="w-full py-3.5 rounded-2xl font-bold text-[14px] text-white transition-all active:scale-95 min-h-[52px]"
        style={{ background: 'linear-gradient(135deg, #F97316, #EA6C0A)', boxShadow: '0 4px 20px rgba(249,115,22,0.3)' }}>
        Continue Training Plan
      </button>
    </div>
  );
}

// ─── View: Plan Complete ──────────────────────────────────────────────────────

function PlanCompleteView({
  plan, profile, fingerprint, onViewProgress,
}: {
  plan: DailyTrainingPlan;
  profile: CognitiveProfile;
  fingerprint: CognitiveFingerprint;
  onViewProgress: () => void;
}) {
  const completedTasks = plan.tasks.filter(t => t.completed && t.score != null);
  const avgScore = completedTasks.length
    ? Math.round(completedTasks.reduce((a, t) => a + (t.score ?? 0), 0) / completedTasks.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/08 p-5 text-center"
        style={{ background: 'rgba(16,185,129,0.06)' }}>
        <div className="text-4xl mb-2">🧠</div>
        <h3 className="text-[18px] font-bold text-white mb-1">Training Complete!</h3>
        <p className="text-[12px] text-[#8BAFD4]">Today's brain training plan finished · Average score: <span className="font-bold text-emerald-400">{avgScore}</span></p>
        <div className="flex items-center justify-center gap-3 mt-3">
          <div className="flex items-center gap-1.5">
            <Flame size={13} className="text-[#F97316]" />
            <span className="text-[12px] font-bold text-[#F97316]">{profile.streakDays} day streak</span>
          </div>
          <div className="w-px h-4 bg-[#1E3A5F]" />
          <div className="flex items-center gap-1.5">
            <Award size={13} className="text-[#8B5CF6]" />
            <span className="text-[12px] font-bold text-[#8B5CF6]">{profile.sessionCount} sessions</span>
          </div>
        </div>
      </div>

      {/* Score breakdown */}
      <div className="grid grid-cols-3 gap-2">
        {completedTasks.map(t => {
          const gc = GAME_CONFIG[t.gameType];
          return (
            <div key={t.id} className="rounded-xl p-3 text-center border border-[#1E3A5F]/40 bg-[#0B1628]/60">
              <div className="text-xl mb-1">{gc.icon}</div>
              <p className="text-[16px] font-bold" style={{ color: gc.color }}>{Math.round(t.score ?? 0)}</p>
              <p className="text-[9px] text-[#4A7AB5] leading-tight">{gc.label}</p>
            </div>
          );
        })}
      </div>

      {/* Cognitive Fingerprint */}
      <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={13} className="text-[#8B5CF6]" />
          <p className="text-[11px] font-bold text-[#8B5CF6] uppercase tracking-wider">Cognitive Fingerprint</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          {[
            { label: 'Dominant Strength', value: fingerprint.dominantStrength, color: '#10B981' },
            { label: 'Developing Domain', value: fingerprint.dominantWeakness, color: '#F97316' },
            { label: 'Training Style', value: fingerprint.trainingStyle, color: '#8B5CF6' },
            { label: 'Adaptation Rate', value: fingerprint.adaptationRate, color: '#38BDF8' },
            { label: 'Consistency', value: fingerprint.consistencyRating, color: '#F59E0B' },
          ].map(f => (
            <div key={f.label} className="rounded-xl p-2.5 border border-[#1E3A5F]/30 bg-[#060D1A]/60">
              <p className="text-[9px] text-[#4A7AB5] mb-0.5">{f.label}</p>
              <p className="font-bold" style={{ color: f.color }}>{f.value}</p>
            </div>
          ))}
          <div className="rounded-xl p-2.5 border border-[#1E3A5F]/30 bg-[#060D1A]/60 col-span-2">
            <p className="text-[9px] text-[#4A7AB5] mb-0.5">Unique Pattern</p>
            <p className="text-[10px] text-[#8BAFD4] italic">{fingerprint.uniquePattern}</p>
          </div>
        </div>
      </div>

      <button onClick={onViewProgress}
        className="w-full py-3 rounded-2xl font-bold text-[13px] text-white border border-[#1E3A5F]/50 hover:border-[#F97316]/40 transition-all active:scale-95 flex items-center justify-center gap-2">
        <BarChart2 size={14} />
        View Progress Trends
      </button>
    </div>
  );
}

// ─── View: Progress Trends ────────────────────────────────────────────────────

function ProgressView({
  snapshots, profile, onBack,
}: {
  snapshots: ProgressSnapshot[];
  profile: CognitiveProfile;
  onBack: () => void;
}) {
  const chartData = snapshots.slice(-14).map(s => ({
    date: s.snapshotDate.slice(5),
    Focus: Math.round(s.focusScore),
    Memory: Math.round(s.mentalEnergy),
    Stability: Math.round(s.stability),
    Flexibility: Math.round(s.flexibility),
  }));

  const radarData = [
    { domain: 'Focus', value: Math.round(profile.focusScore) },
    { domain: 'Memory', value: Math.round(profile.mentalEnergy) },
    { domain: 'Stability', value: Math.round(profile.stability) },
    { domain: 'Flexibility', value: Math.round(profile.flexibility) },
    { domain: 'Reaction', value: Math.max(0, Math.round(100 - (profile.reactionTimeAvg - 200) / 8)) },
  ];

  const personalBests = [
    { label: 'Best Focus', value: Math.round(profile.personalBestFocus), color: '#10B981' },
    { label: 'Best Memory', value: Math.round(profile.personalBestMemory), color: '#8B5CF6' },
    { label: 'Best Flexibility', value: Math.round(profile.personalBestFlexibility), color: '#38BDF8' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="p-2 rounded-xl border border-[#1E3A5F]/50 hover:border-[#F97316]/40 text-[#8BAFD4] hover:text-white transition-all min-h-[40px] min-w-[40px] flex items-center justify-center">
          ←
        </button>
        <div>
          <h3 className="text-[15px] font-bold text-white">Progress Trends</h3>
          <p className="text-[11px] text-[#4A7AB5]">Longitudinal cognitive trajectory</p>
        </div>
      </div>

      {/* Personal bests */}
      <div className="grid grid-cols-3 gap-2">
        {personalBests.map(pb => (
          <div key={pb.label} className="rounded-xl p-3 text-center border border-[#1E3A5F]/40 bg-[#0B1628]/60">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star size={10} style={{ color: pb.color }} />
              <span className="text-[9px] text-[#4A7AB5]">PB</span>
            </div>
            <p className="text-[18px] font-bold" style={{ color: pb.color }}>{pb.value}</p>
            <p className="text-[9px] text-[#4A7AB5]">{pb.label}</p>
          </div>
        ))}
      </div>

      {/* Radar chart */}
      {radarData.length > 0 && (
        <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
          <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider font-medium mb-3">Cognitive Radar</p>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="rgba(30,58,95,0.5)" />
              <PolarAngleAxis dataKey="domain" tick={{ fill: '#4A7AB5', fontSize: 10 }} />
              <Radar name="Profile" dataKey="value" stroke="#F97316" fill="#F97316" fillOpacity={0.15} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Line chart */}
      {chartData.length > 1 && (
        <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
          <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider font-medium mb-3">14-Day Trend</p>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,58,95,0.3)" />
              <XAxis dataKey="date" tick={{ fill: '#4A7AB5', fontSize: 9 }} />
              <YAxis domain={[0, 100]} tick={{ fill: '#4A7AB5', fontSize: 9 }} />
              <Tooltip contentStyle={{ background: '#0B1628', border: '1px solid #1E3A5F', borderRadius: 8, fontSize: 11 }} />
              <Line type="monotone" dataKey="Focus" stroke="#10B981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Memory" stroke="#8B5CF6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Stability" stroke="#38BDF8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Flexibility" stroke="#F97316" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-2">
            {[['Focus','#10B981'],['Memory','#8B5CF6'],['Stability','#38BDF8'],['Flexibility','#F97316']].map(([k,c]) => (
              <div key={k} className="flex items-center gap-1">
                <div className="w-2.5 h-0.5 rounded" style={{ background: c }} />
                <span className="text-[9px] text-[#4A7AB5]">{k}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plateau warning */}
      {profile.plateauDetected && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/08 p-4"
          style={{ background: 'rgba(245,158,11,0.06)' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={13} className="text-amber-400" />
            <p className="text-[12px] font-bold text-amber-400">Plateau Detected — {profile.plateauDomain}</p>
          </div>
          <p className="text-[11px] text-amber-300/80 leading-relaxed">
            Your improvement in this domain has stabilised. The training plan has been automatically adjusted to introduce new variations and re-stimulate neural adaptation.
          </p>
        </div>
      )}

      {/* Streak & consistency */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4 text-center">
          <Flame size={20} className="text-[#F97316] mx-auto mb-1" />
          <p className="text-[24px] font-bold text-white">{profile.streakDays}</p>
          <p className="text-[11px] text-[#4A7AB5]">Day Streak</p>
        </div>
        <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4 text-center">
          <Activity size={20} className="text-[#38BDF8] mx-auto mb-1" />
          <p className="text-[24px] font-bold text-white">{profile.sessionCount}</p>
          <p className="text-[11px] text-[#4A7AB5]">Total Sessions</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type ViewState = 'loading' | 'plan' | 'game' | 'result' | 'complete' | 'progress';

export default function AdaptiveBrainGym() {
  const { user } = useAuth();
  const [view, setView] = useState<ViewState>('loading');
  const [profile, setProfile] = useState<CognitiveProfile | null>(null);
  const [plan, setPlan] = useState<DailyTrainingPlan | null>(null);
  const [snapshots, setSnapshots] = useState<ProgressSnapshot[]>([]);
  const [activeTask, setActiveTask] = useState<TrainingTask | null>(null);
  const [lastResult, setLastResult] = useState<GameResult | null>(null);
  const [lastInsight, setLastInsight] = useState('');
  const [fingerprint, setFingerprint] = useState<CognitiveFingerprint | null>(null);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id ?? 'demo-user';

  // ── Load data ──
  const loadData = useCallback(async () => {
    setView('loading');
    try {
      let prof = await getCognitiveProfile(userId);
      if (!prof) {
        prof = createDefaultProfile(userId);
        await upsertCognitiveProfile(prof);
      }
      setProfile(prof);
      setFingerprint(computeCognitiveFingerprint(prof));

      // Load or generate today's plan
      let todayPlan = await getTodayPlan(userId);
      if (!todayPlan) {
        const sessions = await getRecentGymSessions(userId, 30);
        const plateau = detectPlateau(sessions.map(s => ({ gameType: s.gameType, score: s.score, createdAt: s.createdAt ?? '' })), 'go_no_go');
        const cogState: CognitiveStateLabel = prof.fatigueIndex > 60 ? 'fatigued'
          : prof.focusScore > 70 ? 'focused'
          : prof.stability < 40 ? 'stressed' :'balanced';
        todayPlan = generateDailyPlan(prof, cogState, 0.7, ['Brain Check'], sessions, plateau);
        await saveDailyPlan(todayPlan);
        todayPlan = await getTodayPlan(userId) ?? todayPlan;
      }
      setPlan(todayPlan);

      const snaps = await getProgressSnapshots(userId, 14);
      setSnapshots(snaps);

      setView('plan');
    } catch (e) {
      setError('Failed to load training data. Please try again.');
      setView('plan');
    }
  }, [userId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Start task ──
  const handleStartTask = (task: TrainingTask) => {
    setActiveTask(task);
    setView('game');
  };

  // ── Game complete ──
  const handleGameComplete = async (result: GameResult) => {
    if (!activeTask || !profile || !plan) return;

    // Generate insight
    const insights: Record<GameType, (r: GameResult) => string> = {
      go_no_go: (r) => r.score >= 75
        ? `Strong inhibitory control — your prefrontal cortex is effectively suppressing impulsive responses. Avg RT: ${r.reactionTimeAvg}ms.`
        : r.fatigueDetected
        ? `Fatigue pattern detected in your reaction times. Consider a short break before the next task.`
        : `Focus on reducing false positives — inhibition accuracy is the key metric here. Keep practising.`,
      n_back: (r) => r.score >= 75
        ? `Excellent working memory performance. Maintaining ${r.trialsCorrect}/${r.trialsTotal} correct under cognitive load is a strong result.`
        : `Working memory capacity is trainable. Consistent N-Back practice builds dorsolateral prefrontal cortex density.`,
      task_switching: (r) => r.score >= 75
        ? `High cognitive flexibility — rapid rule adaptation with low switch cost. This reflects strong executive function.`
        : `Cognitive flexibility improves with practice. Focus on the rule indicator before responding.`,
    };
    const insight = insights[activeTask.gameType](result);
    setLastInsight(insight);
    setLastResult(result);

    // Save session
    const session: BrainGymSession = {
      userId,
      sessionDate: new Date().toISOString().split('T')[0],
      gameType: activeTask.gameType,
      difficultyUsed: activeTask.difficulty,
      parametersUsed: DEFAULT_PARAMS[activeTask.difficulty],
      performanceMetrics: {
        score: result.score, accuracy: result.accuracy, reactionTimeAvg: result.reactionTimeAvg,
        trialsTotal: result.trialsTotal, trialsCorrect: result.trialsCorrect,
        durationMs: result.durationMs, difficultyAdjustments: result.difficultyAdjustments,
        fatigueDetected: result.fatigueDetected,
      },
      score: result.score,
      accuracy: result.accuracy,
      reactionTimeAvg: result.reactionTimeAvg,
      trialsTotal: result.trialsTotal,
      trialsCorrect: result.trialsCorrect,
      durationMs: result.durationMs,
      difficultyAdjustments: result.difficultyAdjustments,
      fatigueDetected: result.fatigueDetected,
      eegAvailable: false,
      aiInsight: insight,
      plateauContribution: false,
    };

    const sessionId = await saveBrainGymSession(session);

    // Update profile
    const profileUpdates = updateProfileFromSession(profile, session);
    const updatedProfile = { ...profile, ...profileUpdates };
    await upsertCognitiveProfile({ ...updatedProfile, userId });
    setProfile(updatedProfile);
    setFingerprint(computeCognitiveFingerprint(updatedProfile));

    // Mark task complete in plan
    if (plan.id && sessionId) {
      await markTaskComplete(plan.id, activeTask.id, sessionId, result.score);
    }

    // Refresh plan
    const refreshedPlan = await getTodayPlan(userId);
    if (refreshedPlan) setPlan(refreshedPlan);

    setView('result');
  };

  // ── Continue after result ──
  const handleContinue = async () => {
    const refreshedPlan = await getTodayPlan(userId);
    if (refreshedPlan) {
      setPlan(refreshedPlan);
      if (refreshedPlan.isComplete) {
        // Save progress snapshot
        if (profile) {
          const sessions = await getRecentGymSessions(userId, 30);
          await saveProgressSnapshot(userId, profile, sessions);
          const snaps = await getProgressSnapshots(userId, 14);
          setSnapshots(snaps);
        }
        setView('complete');
      } else {
        setView('plan');
      }
    } else {
      setView('plan');
    }
  };

  // ── Regenerate plan ──
  const handleRegeneratePlan = async () => {
    if (!profile) return;
    setView('loading');
    const sessions = await getRecentGymSessions(userId, 30);
    const plateau = detectPlateau(sessions.map(s => ({ gameType: s.gameType, score: s.score, createdAt: s.createdAt ?? '' })), 'go_no_go');
    const cogState: CognitiveStateLabel = profile.fatigueIndex > 60 ? 'fatigued'
      : profile.focusScore > 70 ? 'focused' :'balanced';
    const newPlan = generateDailyPlan(profile, cogState, 0.7, ['Brain Check'], sessions, plateau);
    await saveDailyPlan(newPlan);
    const saved = await getTodayPlan(userId);
    if (saved) setPlan(saved);
    setView('plan');
  };

  // ── Render ──
  return (
    <div className="rounded-2xl border border-[#1E3A5F]/50 bg-[#060D1A]/80 overflow-hidden"
      style={{ boxShadow: '0 0 40px rgba(249,115,22,0.04)' }}>
      {/* Header */}
      <div className="px-4 sm:px-5 py-4 border-b border-[#1E3A5F]/40 flex items-center justify-between"
        style={{ background: 'linear-gradient(135deg, rgba(11,22,40,0.9), rgba(6,13,26,0.95))' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.25)' }}>
            <Brain size={18} className="text-[#F97316]" />
          </div>
          <div>
            <h2 className="text-[14px] font-bold text-white">Adaptive Brain Gym</h2>
            <p className="text-[10px] text-[#4A7AB5]">Closed-loop cognitive training · Personalised</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {view === 'plan' && plan && !plan.isComplete && (
            <button onClick={handleRegeneratePlan}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] text-[#8BAFD4] border border-[#1E3A5F]/50 hover:border-[#F97316]/40 hover:text-[#F97316] transition-all min-h-[36px]">
              <RefreshCw size={11} />
              <span className="hidden sm:inline">Regenerate</span>
            </button>
          )}
          {(view === 'complete' || view === 'result') && (
            <button onClick={() => setView('progress')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] text-[#8BAFD4] border border-[#1E3A5F]/50 hover:border-[#8B5CF6]/40 hover:text-[#8B5CF6] transition-all min-h-[36px]">
              <TrendingUp size={11} />
              <span className="hidden sm:inline">Progress</span>
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5">
        {view === 'loading' && (
          <div className="flex flex-col items-center justify-center py-14 gap-5">
            {/* Neuron network animation */}
            <div className="relative w-20 h-20">
              {/* Outer orbit ring */}
              <div className="absolute inset-0 rounded-full border border-[#F97316]/20"
                style={{ animation: 'spin 4s linear infinite' }}>
                {[0, 90, 180, 270].map((deg) => (
                  <div key={deg} className="absolute w-2 h-2 rounded-full bg-[#F97316]/50"
                    style={{ top: '50%', left: '50%', transform: `rotate(${deg}deg) translateX(36px) translateY(-50%)` }} />
                ))}
              </div>
              {/* Inner pulse ring */}
              <div className="absolute inset-3 rounded-full border border-[#8B5CF6]/30"
                style={{ animation: 'ping 2s ease-in-out infinite' }} />
              {/* Center brain */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Brain size={28} className="text-[#F97316]" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
              </div>
            </div>
            {/* Brainwave bar animation */}
            <div className="flex items-end gap-1 h-8">
              {[4,7,12,9,5,10,7,4,8,11,6,9].map((h, i) => (
                <div key={i} className="w-1 rounded-full bg-[#F97316]"
                  style={{
                    height: `${h}px`,
                    animation: `pulse 1.2s ease-in-out ${i * 0.1}s infinite`,
                    opacity: 0.6,
                  }} />
              ))}
            </div>
            <div className="text-center space-y-1">
              <p className="text-[13px] font-semibold text-white">Calibrating Neural Profile</p>
              <p className="text-[11px] text-[#4A7AB5]">Generating personalised training plan…</p>
            </div>
            <style>{`
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
          </div>
        )}

        {error && view === 'plan' && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/25">
            <AlertTriangle size={13} className="text-red-400 flex-shrink-0" />
            <p className="text-[11px] text-red-300">{error}</p>
          </div>
        )}

        {view === 'plan' && plan && profile && (
          <DailyPlanView plan={plan} profile={profile} onStartTask={handleStartTask} />
        )}

        {view === 'game' && activeTask && profile && (
          <GameSessionView
            task={activeTask}
            profile={profile}
            onComplete={handleGameComplete}
            onExit={() => setView('plan')}
          />
        )}

        {view === 'result' && activeTask && lastResult && profile && (
          <SessionResultView
            task={activeTask}
            result={lastResult}
            profile={profile}
            insight={lastInsight}
            onContinue={handleContinue}
          />
        )}

        {view === 'complete' && plan && profile && fingerprint && (
          <PlanCompleteView
            plan={plan}
            profile={profile}
            fingerprint={fingerprint}
            onViewProgress={() => setView('progress')}
          />
        )}

        {view === 'progress' && profile && (
          <ProgressView
            snapshots={snapshots}
            profile={profile}
            onBack={() => setView(plan?.isComplete ? 'complete' : 'plan')}
          />
        )}
      </div>
    </div>
  );
}
